import { Fragment, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import routeManifest from "@/data/route-manifest.json";
import { PageViewAnalyticsService } from "@/services/analytics/PageViewAnalyticsService";
import {
  PageViewsPeriod,
  PageViewTotalRow,
  RouteKind,
  RouteManifestEntry,
  TrafficRow,
} from "@/types/admin/page-views";
import { formatNumber, ROUTE_KIND_CHIP_STYLES, ROUTE_KIND_LABELS } from "./constants";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const ROUTES = routeManifest.routes as RouteManifestEntry[];

// Static routes stay as their own row; every other URL rolls up under the static
// prefix of the dynamic route it belongs to (/live-agent/*, /genie-hub/my-genies/*…).
const STATIC_ROUTE_KINDS = new Map<string, RouteKind>(
  ROUTES.filter((r) => !/[:*]/.test(r.path)).map((r) => [r.path, r.kind]),
);
const DYNAMIC_PREFIXES = [
  ...new Map(
    ROUTES.flatMap((r) => {
      const dynamicAt = r.path.search(/\/[:*]/);
      return dynamicAt === -1 ? [] : [[r.path.slice(0, dynamicAt + 1), r.kind] as const];
    }),
  ).entries(),
].sort((a, b) => b[0].length - a[0].length);

function groupRows(items: PageViewTotalRow[]): TrafficRow[] {
  const groups = new Map<string, { kind: RouteKind; children: PageViewTotalRow[] }>();
  const rows: TrafficRow[] = [];

  for (const row of items) {
    const staticKind = STATIC_ROUTE_KINDS.get(row.path);
    const prefix = staticKind ? undefined : DYNAMIC_PREFIXES.find(([p]) => row.path.startsWith(p));
    if (!prefix) {
      rows.push({ key: row.path, path: row.path, kind: staticKind ?? null, view_count: row.view_count });
      continue;
    }
    const [key, kind] = prefix;
    const group = groups.get(key) ?? { kind, children: [] };
    group.children.push(row);
    groups.set(key, group);
  }

  for (const [key, { kind, children }] of groups) {
    rows.push({
      key,
      path: `${key}*`,
      kind,
      view_count: children.reduce((sum, c) => sum + c.view_count, 0),
      children: [...children].sort((a, b) => b.view_count - a.view_count),
    });
  }

  return rows.sort((a, b) => b.view_count - a.view_count);
}

const KindChip = ({ kind }: { kind: RouteKind | null }) =>
  kind ? (
    <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${ROUTE_KIND_CHIP_STYLES[kind]}`}>
      {ROUTE_KIND_LABELS[kind]}
    </span>
  ) : (
    <span className="inline-flex shrink-0 items-center rounded-full border border-dashed border-[#E5EAF0] px-2 py-0.5 text-[11px] font-medium text-gray-500">
      Unknown route
    </span>
  );

const TrafficTab = ({ period }: { period: PageViewsPeriod }) => {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { data, isFetching } = useQuery({
    queryKey: ["page-view-totals", period, search],
    queryFn: () => PageViewAnalyticsService.totalsByPath({ period, search: search.trim() || undefined }),
  });

  const rows = useMemo(() => groupRows(data?.items ?? []), [data?.items]);
  const totalViews = useMemo(() => rows.reduce((sum, r) => sum + r.view_count, 0), [rows]);

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="overflow-hidden rounded-2xl border border-[#E5EAF0] bg-white shadow-sm">
      <div className="flex flex-col gap-3 border-b border-[#E5EAF0] px-4 py-3 sm:flex-row sm:items-center sm:justify-between md:px-5">
        <div className="flex items-center gap-2.5">
          <GenieIcon name="eye" size={20} gradientEnabled secondaryColor="#FF4DB8" label="" />
          <span className="text-sm font-semibold text-[#222B45]">
            {formatNumber(rows.length)} page{rows.length === 1 ? "" : "s"}
          </span>
          <span className="text-sm text-[#64748B]">· {formatNumber(totalViews)} views</span>
          {isFetching && <Loader2 className="h-4 w-4 animate-spin text-genie-primary" />}
        </div>
        <div className="relative sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Filter by path…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-10 border-[#E5EAF0] pl-9 text-base focus-visible:ring-genie-primary/30 sm:text-sm"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5EAF0] bg-[#F9FAFC] text-left text-xs font-medium uppercase tracking-wide text-[#64748B]">
              <th className="px-4 py-2.5 md:px-5">Path</th>
              <th className="px-4 py-2.5 md:px-5">Kind</th>
              <th className="px-4 py-2.5 text-right md:px-5">Views</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isGroup = !!row.children;
              const isExpanded = expanded.has(row.key);
              return (
                <Fragment key={row.key}>
                  <tr
                    className={`border-b border-[#E5EAF0] last:border-0 ${isGroup ? "cursor-pointer hover:bg-[#F9FAFC]" : ""}`}
                    onClick={isGroup ? () => toggle(row.key) : undefined}
                  >
                    <td className="px-4 py-3 md:px-5">
                      <span className="inline-flex items-center gap-1.5 font-mono text-[13px] text-[#222B45]">
                        {isGroup &&
                          (isExpanded ? (
                            <ChevronDown className="h-3.5 w-3.5 text-[#64748B]" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5 text-[#64748B]" />
                          ))}
                        <span className="break-all">{row.path}</span>
                        {isGroup && (
                          <span className="whitespace-nowrap font-sans text-xs text-[#64748B]">
                            ({row.children!.length} URL{row.children!.length === 1 ? "" : "s"})
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 md:px-5">
                      <KindChip kind={row.kind} />
                    </td>
                    <td className="px-4 py-3 text-right font-semibold tabular-nums text-[#222B45] md:px-5">
                      {formatNumber(row.view_count)}
                    </td>
                  </tr>
                  {isExpanded &&
                    row.children!.map((child) => (
                      <tr key={child.path} className="border-b border-[#E5EAF0] bg-[#F9FAFC]">
                        <td colSpan={2} className="break-all py-2 pl-10 pr-4 font-mono text-xs text-[#64748B] md:pl-11">
                          {child.path}
                        </td>
                        <td className="px-4 py-2 text-right text-xs tabular-nums text-[#64748B] md:px-5">
                          {formatNumber(child.view_count)}
                        </td>
                      </tr>
                    ))}
                </Fragment>
              );
            })}
            {!isFetching && rows.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-12 text-center text-sm text-[#64748B]">
                  No page views recorded for this range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TrafficTab;
